// Record Types
export interface Record {
  active_space_group_members_count?: number;
  active_space_members_count?: number;
  avatar_url?: string;
  custom_emoji_dark_url?: string;
  custom_emoji_url?: string;
  emoji?: string;
  id: number;
  index?: number;
  name: string;
  tagged_members_count?: number;
  type?: string;
}

// Specific record types
export interface CommunityMember extends Record {
  type: "community_member";
  // Add any community member specific fields here
}

export interface Space extends Record {
  type: "space";
  // Add any space specific fields here
}

export interface SpaceGroup extends Record {
  type: "space_group";
  // Add any space group specific fields here
}

export interface MemberTag extends Record {
  type: "member_tag";
  // Add any member tag specific fields here
}

// Union type for all records
export type RecordType = CommunityMember | Space | SpaceGroup | MemberTag;

// Record types constants
export const RECORD_TYPES = {
  COMMUNITY_MEMBER: "community_member",
  SPACE: "space",
  SPACE_GROUP: "space_group",
  MEMBER_TAG: "member_tag",
} as const;

// Value formats
export interface SimpleValue extends Array<number> {}

export interface ComplexValue {
  member_tags_ids: number[];
  members_ids: number[];
  space_groups_ids: number[];
  spaces_ids: number[];
}

export type Value = SimpleValue | ComplexValue | null;

// Selection State
export interface SelectionState {
  communityMembers: CommunityMember[];
  errorMessage: string | null;
  isLoading: boolean;
  memberCount: number;
  memberTags: MemberTag[];
  spaceGroups: SpaceGroup[];
  spaces: Space[];
  value: Value;
}

// Action Types
interface SelectionActions {
  ADD: "ADD";
  INITIAL_FETCH_FAILURE: "INITIAL_FETCH_FAILURE";
  INITIAL_FETCH_START: "INITIAL_FETCH_START";
  INITIAL_FETCH_SUCCESS: "INITIAL_FETCH_SUCCESS";
  REMOVE: "REMOVE";
  RESET: "RESET";
}

// Action Payloads
export interface AddPayload {
  includeAllResultTypes: boolean;
  record: RecordType;
  shouldUpdateValue: boolean;
}

export interface RemovePayload {
  includeAllResultTypes: boolean;
  record: RecordType;
}

export interface InitialFetchStartPayload {
  communityMemberIds: number[];
  memberTagIds: number[];
  spaceGroupIds: number[];
  spaceIds: number[];
}

export interface InitialFetchSuccessPayload {
  communityMemberIds: number[];
  memberTagIds: number[];
  spaceGroupIds: number[];
  spaceIds: number[];
}

export interface InitialFetchFailurePayload {
  communityMemberIds: number[];
  errorMessage: string;
  memberTagIds: number[];
  spaceGroupIds: number[];
  spaceIds: number[];
}

export interface ResetPayload {
  includeAllResultTypes: boolean;
}

// Action Union Type
export interface AddAction {
  payload: AddPayload;
  type: SelectionActions["ADD"];
}

export interface RemoveAction {
  payload: RemovePayload;
  type: SelectionActions["REMOVE"];
}

export interface InitialFetchStartAction {
  payload: InitialFetchStartPayload;
  type: SelectionActions["INITIAL_FETCH_START"];
}

export interface InitialFetchSuccessAction {
  payload: InitialFetchSuccessPayload;
  type: SelectionActions["INITIAL_FETCH_SUCCESS"];
}

export interface InitialFetchFailureAction {
  payload: InitialFetchFailurePayload;
  type: SelectionActions["INITIAL_FETCH_FAILURE"];
}

export interface ResetAction {
  payload: ResetPayload;
  type: SelectionActions["RESET"];
}

export type SelectionAction =
  | AddAction
  | RemoveAction
  | InitialFetchStartAction
  | InitialFetchSuccessAction
  | InitialFetchFailureAction
  | ResetAction;

// Component Variants
export const VARIANTS = {
  SIMPLE: "simple",
  MODAL: "modal",
  BUTTON: "button",
} as const;

export type Variant = (typeof VARIANTS)[keyof typeof VARIANTS];

export interface ApiResponse<T> {
  records: T[];
}
